import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Skeleton,
  IconButton,
  Divider,
  Button,
  Card,
  CardContent,
  Tooltip
} from '@mui/material';
import {
  Business as BusinessIcon,
  Work as WorkIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { buscarEstatisticasDashboard, DashboardStats, buscarVagasPorEmpresa, VagasPorEmpresa } from '../../services/prefeitura';
import { useTheme } from '../../contexts/ThemeContext';
import { normalizarNomeEmpresa } from '../../utils/normalization';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend as ChartLegend
} from 'chart.js';
import { Bar as ChartBar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  ChartLegend
);

interface StatCard {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  trend?: string;
}

const Dashboard: React.FC = () => {
  const location = useLocation();
  const { darkMode, toggleDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({
    totalEmpresas: 0,
    vagasAtivas: 0,
    totalCandidatos: 0,
    totalCandidaturas: 0
  });
  const [vagasPorEmpresa, setVagasPorEmpresa] = useState<VagasPorEmpresa[]>([]);
  const [loadingCharts, setLoadingCharts] = useState(true);
  const [expandedVagas, setExpandedVagas] = useState(false);


  useEffect(() => {
    carregarEstatisticas();
    carregarVagasPorEmpresa();
  }, []);
  
  // Recarregar dados quando voltar para a dashboard
  useEffect(() => {
    if (location.pathname === '/dashboard-prefeitura') {
      carregarEstatisticas();
      carregarVagasPorEmpresa();
    }
  }, [location]);

  const carregarEstatisticas = async () => {
    setLoading(true);
    try {
      const dados = await buscarEstatisticasDashboard();
      setStats(dados);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    } finally {
      setLoading(false);
    }
  };

  const carregarVagasPorEmpresa = async () => {
    setLoadingCharts(true);
    try {
      const dados = await buscarVagasPorEmpresa();
      setVagasPorEmpresa(dados);
    } catch (error) {
      console.error('Erro ao carregar vagas por empresa:', error);
    } finally {
      setLoadingCharts(false);
    }
  };

  const formatarNumero = (num: number): string => {
    return num.toLocaleString('pt-BR');
  };

  // Referência usada apenas para dimensionar a barra: consideramos 5 candidaturas
  // por vaga como um cenário de concorrência "cheia".
  const REFERENCIA_CANDIDATURAS_POR_VAGA = 5;

  // Média de candidaturas por vaga ativa (protegida contra divisão por zero).
  // OBS: este número NÃO é uma taxa de ocupação — uma mesma vaga pode receber
  // várias candidaturas, por isso o valor pode ultrapassar 1 candidatura/vaga.
  const mediaCandidaturasPorVaga =
    stats.vagasAtivas > 0 ? stats.totalCandidaturas / stats.vagasAtivas : 0;

  // Preenchimento da barra sempre entre 0% e 100%.
  const preenchimentoBarraCandidaturas = Math.min(
    Math.max((mediaCandidaturasPorVaga / REFERENCIA_CANDIDATURAS_POR_VAGA) * 100, 0),
    100
  );

  const mediaCandidaturasFormatada = mediaCandidaturasPorVaga.toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  });

  const statsCards: StatCard[] = [
    {
      title: 'Empresas Cadastradas',
      value: formatarNumero(stats.totalEmpresas),
      icon: <BusinessIcon sx={{ fontSize: 48 }} />,
      color: '#4A5568', // Azul acinzentado mais escuro
      trend: ''
    },
    {
      title: 'Vagas Ativas',
      value: formatarNumero(stats.vagasAtivas),
      icon: <WorkIcon sx={{ fontSize: 48 }} />,
      color: '#5A6C7D', // Azul acinzentado médio
      trend: ''
    },
    {
      title: 'Candidatos Cadastrados',
      value: formatarNumero(stats.totalCandidatos),
      icon: <PeopleIcon sx={{ fontSize: 48 }} />,
      color: '#6B7D8A', // Azul acinzentado claro
      trend: ''
    },
    {
      title: 'Total de Candidaturas',
      value: formatarNumero(stats.totalCandidaturas),
      icon: <AssignmentIcon sx={{ fontSize: 48 }} />,
      color: '#7C8E97', // Azul acinzentado mais claro
      trend: ''
    }
  ];

  return (
    <Box sx={{ width: '100%', overflow: 'hidden', px: { xs: 1.5, sm: 1 }, position: 'relative' }}>
      {/* Mensagem de boas-vindas */}
      <Box sx={{ 
        mb: 2, 
        mt: 1, 
        display: 'flex', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        justifyContent: 'space-between',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 }
      }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 500, 
            color: darkMode ? '#fff' : '#333',
            fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2.125rem' },
            lineHeight: { xs: 1.3, sm: 1.235 },
            textAlign: { xs: 'center', sm: 'left' },
            wordBreak: 'break-word',
            hyphens: 'auto'
          }}
        >
          <Box component="span" sx={{ display: { xs: 'block', sm: 'inline' } }}>
            Olá, Bem-Vindo ao
          </Box>
          {' '}
          <Box component="span" sx={{ display: { xs: 'block', sm: 'inline' } }}>
            Portal de Vagas
          </Box>
        </Typography>
        
        {/* Botão de tema */}
        <Tooltip 
          title={darkMode ? "Alternar para Modo Claro" : "Alternar para Modo Escuro"} 
          enterDelay={1000}
          placement={window.innerWidth < 600 ? "bottom" : "left"}
          arrow
        >
          <IconButton
            onClick={toggleDarkMode}
            sx={{
              backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)',
              alignSelf: { xs: 'center', sm: 'flex-start' },
              mt: { xs: 0.5, sm: 0 },
              '&:hover': {
                backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.08)'
              }
            }}
          >
            {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Cards de Estatísticas */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { 
          xs: '1fr', 
          sm: 'repeat(2, 1fr)', 
          md: 'repeat(4, 1fr)' 
        },
        gap: { xs: 2, sm: 2.5, md: 2 }, 
        mb: 3
      }}>
        {statsCards.map((stat, index) => (
          <Paper
            key={index}
            elevation={3}
            sx={{
              p: { xs: 2.5, sm: 3 },
              position: 'relative',
              overflow: 'hidden',
              borderRadius: 2,
              background: `linear-gradient(135deg, ${stat.color} 0%, ${stat.color}dd 40%, ${stat.color}bb 100%)`,
              color: 'white',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: { xs: '140px', sm: '130px' },
              cursor: 'default',
            }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                <Skeleton variant="circular" width={50} height={50} sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.2)' }} />
                <Skeleton variant="text" width="80%" sx={{ mb: 1, bgcolor: 'rgba(255,255,255,0.2)' }} />
                <Skeleton variant="text" width="60%" height={40} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
              </Box>
            ) : (
              <>
                <Box sx={{ 
                  mb: 1.5
                }}>
                  {stat.icon}
                </Box>
                <Typography variant="body2" sx={{ 
                  fontSize: '0.9rem', 
                  mb: 1, 
                  textAlign: 'center', 
                  opacity: 0.9
                }}>
                  {stat.title}
                </Typography>
                <Typography variant="h3" sx={{ 
                  fontWeight: 700, 
                  fontSize: '2.5rem'
                }}>
                  {stat.value}
                </Typography>
              </>
            )}
          </Paper>
        ))}
      </Box>

      {/* Segunda linha - Gráficos e Estatísticas */}
      <Box sx={{ 
        mt: { xs: 2, md: 3 }, 
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 2.5, md: 2 },
        pb: 2
      }}>
        {/* Gráfico de Barras Horizontais - Vagas por Empresa */}
        <Paper elevation={2} sx={{ 
          p: { xs: 2, sm: 3 }, 
          borderRadius: 2, 
          backgroundColor: darkMode ? '#424242' : '#ffffff',
          flex: { xs: '1', md: '1' },
          width: { xs: '100%', md: '50%' },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          height: {
            xs: 'auto',
            md: expandedVagas ? 'auto' : 'calc(100vh - 335px)'
          },
          minHeight: { xs: 400, sm: 450, md: 550 },
          transition: 'all 0.3s ease-in-out',
          cursor: 'default',
          '&:hover': {
            transform: { xs: 'none', md: 'scale(1.02)' },
            boxShadow: darkMode 
              ? '0 8px 32px rgba(0, 0, 0, 0.6), 0 0 20px rgba(66, 66, 66, 0.4)' 
              : '0 8px 32px rgba(0, 0, 0, 0.15), 0 0 20px rgba(0, 0, 0, 0.08)',
            zIndex: 10,
            position: 'relative'
          }
        }}>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
                Vagas por empresa
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'white', 
                  fontWeight: 600,
                  backgroundColor: '#4caf50',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.9rem'
                }}
              >
                {vagasPorEmpresa.reduce((total, empresa) => total + empresa.vagas, 0)} vagas
              </Typography>
            </Box>
            {!loadingCharts && vagasPorEmpresa.length > 0 && (
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                Exibindo {expandedVagas ? vagasPorEmpresa.length : Math.min(20, vagasPorEmpresa.length)} de {vagasPorEmpresa.length} empresas
              </Typography>
            )}
          </Box>
          {loadingCharts ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <Skeleton variant="rectangular" width="90%" height="80%" />
            </Box>
          ) : vagasPorEmpresa.length > 0 ? (
            <Box sx={{ 
              flex: 1,
              overflow: 'auto',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: darkMode ? '#2a2a2a' : '#f1f1f1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: darkMode ? '#555' : '#888',
                borderRadius: '4px',
                '&:hover': {
                  background: darkMode ? '#666' : '#555',
                }
              }
            }}>
              <Box sx={{ 
                height: expandedVagas ? Math.max(400, vagasPorEmpresa.length * 40) : Math.min(800, 20 * 40),
                minHeight: 400,
                transition: 'height 0.3s ease-in-out'
              }}>
                <ChartBar
                id="vagas-por-empresa"
                data={{
                  labels: vagasPorEmpresa
                    .sort((a, b) => b.vagas - a.vagas)
                    .slice(0, expandedVagas ? vagasPorEmpresa.length : 20)
                    .map(empresa => normalizarNomeEmpresa(empresa.nome)),
                  datasets: [{
                    data: vagasPorEmpresa
                      .sort((a, b) => b.vagas - a.vagas)
                      .slice(0, expandedVagas ? vagasPorEmpresa.length : 20)
                      .map(empresa => empresa.vagas),
                    backgroundColor: vagasPorEmpresa
                      .sort((a, b) => b.vagas - a.vagas)
                      .slice(0, expandedVagas ? vagasPorEmpresa.length : 20)
                      .map((_, index) => {
                        const displayedCount = expandedVagas ? vagasPorEmpresa.length : Math.min(20, vagasPorEmpresa.length);
                        const ratio = displayedCount > 1 ? index / (displayedCount - 1) : 0;
                        
                        // Gradiente de azul escuro para azul claro (similar à imagem)
                        const r = Math.round(27 + (135 - 27) * ratio);
                        const g = Math.round(78 + (206 - 78) * ratio);
                        const b = Math.round(142 + (235 - 142) * ratio);
                        
                        return `rgb(${r}, ${g}, ${b})`;
                      }),
                    borderRadius: 4,
                    barThickness: 25,
                    maxBarThickness: 30,
                    barPercentage: 0.7,
                    categoryPercentage: 0.8,
                  }]
                }}
                options={{
                  indexAxis: 'y' as const,
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false
                    },
                    tooltip: {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      padding: 10,
                      cornerRadius: 5,
                      callbacks: {
                        label: (context) => `${context.parsed.x} vagas`
                      }
                    }
                  },
                  scales: {
                    x: {
                      beginAtZero: true,
                      border: {
                        display: false
                      },
                      grid: {
                        display: false
                      },
                      ticks: {
                        display: false
                      }
                    },
                    y: {
                      border: {
                        display: false
                      },
                      grid: {
                        display: false
                      },
                      ticks: {
                        font: {
                          size: 14,
                          weight: 500 as const
                        },
                        color: darkMode ? '#fff' : '#333',
                        padding: 8
                      }
                    }
                  },
                  layout: {
                    padding: {
                      left: 10,
                      right: 60 // Aumentado para dar espaço aos números
                    }
                  },
                  animation: {
                    onComplete: () => {
                      const ctx = ChartJS.getChart('vagas-por-empresa')?.ctx;
                      if (ctx) {
                        ctx.save();
                        ctx.font = 'bold 14px Arial';
                        ctx.fillStyle = darkMode ? '#fff' : '#333';
                        ctx.textAlign = 'left';
                        ctx.textBaseline = 'middle';
                        
                        const chart = ChartJS.getChart('vagas-por-empresa');
                        if (chart) {
                          const meta = chart.getDatasetMeta(0);
                          const data = vagasPorEmpresa
                            .sort((a, b) => b.vagas - a.vagas)
                            .slice(0, expandedVagas ? vagasPorEmpresa.length : 20);
                          
                          meta.data.forEach((bar, index) => {
                            const dataIndex = index;
                            if (dataIndex < data.length) {
                              const value = data[dataIndex].vagas;
                              const x = bar.x + 10; // Posição à direita da barra
                              const y = bar.y;
                              ctx.fillText(`${value} vagas`, x, y);
                            }
                          });
                        }
                        ctx.restore();
                      }
                    }
                  }
                }}
              />
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <Typography variant="body2" color="text.secondary">
                Nenhum dado disponível
              </Typography>
            </Box>
          )}
          
          {/* Botão Ver Mais/Ver Menos */}
          {vagasPorEmpresa.length > 20 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                onClick={() => setExpandedVagas(!expandedVagas)}
                sx={{
                  textTransform: 'none',
                  color: darkMode ? '#90caf9' : '#1976d2',
                  '&:hover': {
                    backgroundColor: darkMode ? 'rgba(144, 202, 249, 0.08)' : 'rgba(25, 118, 210, 0.08)'
                  }
                }}
              >
                {expandedVagas ? 'Ver Menos' : 'Ver Mais'}
              </Button>
            </Box>
          )}
        </Paper>

        {/* Card Comparativo Candidatos vs Vagas */}
        <Paper elevation={2} sx={{ 
          p: { xs: 2, sm: 3 }, 
          borderRadius: 2, 
          backgroundColor: darkMode ? '#424242' : '#ffffff',
          flex: { xs: '1', md: '1' },
          width: { xs: '100%', md: '50%' },
          height: { xs: 'auto', md: 'calc(100vh - 335px)' },
          minHeight: { xs: '400px', sm: '450px', md: '550px' },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'visible',
          transition: 'all 0.3s ease-in-out',
          cursor: 'default',
          '&:hover': {
            transform: { xs: 'none', md: 'scale(1.02)' },
            boxShadow: darkMode 
              ? '0 8px 32px rgba(0, 0, 0, 0.6), 0 0 20px rgba(66, 66, 66, 0.4)' 
              : '0 8px 32px rgba(0, 0, 0, 0.15), 0 0 20px rgba(0, 0, 0, 0.08)',
            zIndex: 10,
            position: 'relative'
          }
        }}>
            <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600, mb: 2 }}>
              Candidatos e vagas ativas
            </Typography>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              {/* Gráfico de barras comparativo vertical */}
              <Box sx={{ 
                flex: 1, 
                minHeight: 200, 
                display: 'flex', 
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'center', md: 'flex-start' }, 
                gap: 2, 
                pt: 0 
              }}>
                <Box sx={{ 
                  flex: 1, 
                  height: { xs: '200px', sm: '250px', md: '300px', lg: '100%' },
                  width: '100%',
                  minWidth: 0,
                  overflow: 'visible'
                }}>
                  <ChartBar
                    data={{
                      labels: ['Candidatos', 'Vagas', 'Candidaturas'],
                      datasets: [{
                        label: 'Total',
                        data: [stats.totalCandidatos, stats.vagasAtivas, stats.totalCandidaturas],
                        backgroundColor: ['#2196f3', '#4caf50', '#ff9800'],
                        borderRadius: 8,
                        maxBarThickness: 60,
                        barPercentage: 0.5,
                        categoryPercentage: 0.8
                      }]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: false
                        },
                        tooltip: {
                          backgroundColor: 'rgba(0, 0, 0, 0.8)',
                          padding: 10,
                          cornerRadius: 5,
                          callbacks: {
                            label: (context) => {
                              const label = context.label || '';
                              const value = context.parsed.y || 0;
                              return `${label}: ${value.toLocaleString('pt-BR')}`;
                            }
                          }
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          border: {
                            display: false
                          },
                          grid: {
                            display: false
                          },
                          ticks: {
                            display: false
                          }
                        },
                        x: {
                          border: {
                            display: false
                          },
                          grid: {
                            display: false
                          },
                          ticks: {
                            display: false
                          }
                        }
                      },
                      layout: {
                        padding: {
                          left: 10,
                          right: 40,
                          top: 20,
                          bottom: 20
                        }
                      }
                    }}
                  />
                </Box>
                {/* Legenda ao lado direito */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'row', md: 'column' }, 
                  flexWrap: { xs: 'wrap', md: 'nowrap' },
                  justifyContent: { xs: 'center', md: 'flex-start' },
                  gap: { xs: 1, md: 1.2 }, 
                  minWidth: { xs: 'auto', sm: 140, md: 160, lg: 180 },
                  mt: { xs: 1, sm: 2, lg: '-3%' }, 
                  mr: { xs: 0, sm: 1, md: 2, lg: 8 },
                  width: { xs: '100%', md: 'auto' },
                  px: { xs: 1, md: 0 }
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: { xs: 'center', md: 'flex-start' }, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, minWidth: '50px', textAlign: 'right' }}>
                      {stats.totalCandidatos.toLocaleString('pt-BR')}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.85rem' }, 
                        backgroundColor: '#2196f3',
                        color: 'white',
                        px: { xs: 0.8, sm: 1.2 },
                        py: { xs: 0.3, sm: 0.4 },
                        borderRadius: 1,
                        whiteSpace: 'nowrap',
                        minWidth: { xs: '90px', sm: '110px' },
                        textAlign: 'center'
                      }}
                    >
                      Candidatos
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: { xs: 'center', md: 'flex-start' }, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, minWidth: '50px', textAlign: 'right' }}>
                      {stats.vagasAtivas.toLocaleString('pt-BR')}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.85rem' }, 
                        backgroundColor: '#4caf50',
                        color: 'white',
                        px: { xs: 0.8, sm: 1.2 },
                        py: { xs: 0.3, sm: 0.4 },
                        borderRadius: 1,
                        whiteSpace: 'nowrap',
                        minWidth: { xs: '90px', sm: '110px' },
                        textAlign: 'center'
                      }}
                    >
                      Vagas
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: { xs: 'center', md: 'flex-start' }, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, minWidth: '50px', textAlign: 'right' }}>
                      {stats.totalCandidaturas.toLocaleString('pt-BR')}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.85rem' }, 
                        backgroundColor: '#ff9800',
                        color: 'white',
                        px: { xs: 0.8, sm: 1.2 },
                        py: { xs: 0.3, sm: 0.4 },
                        borderRadius: 1,
                        whiteSpace: 'nowrap',
                        minWidth: { xs: '90px', sm: '110px' },
                        textAlign: 'center'
                      }}
                    >
                      Candidaturas
                    </Typography>
                  </Box>
                </Box>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              {/* Média de Candidaturas por Vaga */}
              <Box sx={{ mb: 2, mt: 8 }}>
                <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, mb: 0.5 }}>
                  Média de Candidaturas por Vaga
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontSize: '0.8rem', color: 'text.secondary', mb: 2 }}
                >
                  Referência da barra: {REFERENCIA_CANDIDATURAS_POR_VAGA} candidaturas por vaga ativa
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ 
                      height: 24, 
                      backgroundColor: darkMode ? '#333' : '#e0e0e0',
                      borderRadius: 2,
                      overflow: 'hidden',
                      position: 'relative'
                    }}>
                      <Box sx={{ 
                        width: `${preenchimentoBarraCandidaturas.toFixed(0)}%`,
                        height: '100%',
                        background: 'linear-gradient(90deg, #00a8ff 0%, #00ff88 100%)',
                        transition: 'width 0.5s ease-in-out',
                        boxShadow: '0 0 10px rgba(0, 255, 136, 0.5)',
                        filter: 'drop-shadow(0 0 6px #00ff8050) drop-shadow(0 0 12px #00ff8020)'
                      }} />
                    </Box>
                  </Box>
                  <Typography variant="body1" sx={{ minWidth: 50, fontWeight: 600, fontSize: '1.1rem' }}>
                    {mediaCandidaturasFormatada}x
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
      </Box>
    </Box>
  );
};

export default Dashboard;